<template>
  <div class="conditions-dashboard-main">
    <div class="conditions-dashboard__toolbar">
      <button
        class="mini-toggle"
        :class="{ 'mini-toggle--on': groupByLoans }"
        type="button"
        @click="toggleGroupByLoans"
        :title="groupByLoans ? 'Switch to Pipeline view' : 'Group by Loan #'"
      >
        <i class="fa-duotone" :class="groupByLoans ? 'fa-layer-group' : 'fa-list'"></i>
        <span>{{ groupByLoans ? "Group by Loans" : "Pipeline" }}</span>
      </button>

      <div class="meta">
        <i class="fa-duotone fa-layer-group"></i>
        Showing <span class="meta-strong">{{ safeGridItems.length }}</span>
        of <span class="meta-strong">{{ Number(total || 0) }}</span>
      </div>
    </div>

    <div class="conditions-dashboard__container" v-if="groupByLoans">
      <ConditionsLoanGrouped
        :items="filteredAllItems"
        :total="total"
        :photo-url="photoUrl"
        :loading="!effectiveColumnsLoaded"
        :is-photo-loading="isPhotoLoading"
        :close-pill-class-fn="closePillClassFn"
        :search-text="searchText"
        @search-input="onSearchTextChanged"
        @search-clear="clearSearch"
        @open-loan="openLoanTasks"
        @open-encompass="openEncompassWeb"
        @open-video="openVideo"
        @open-teams="openInTeams"
        @open-email="openEmail"
        @img-error="onUwImgError"
      />
    </div>

    <div class="conditions-dashboard__container" v-else-if="viewMode === 'grid'">
      <ConditionsGrid
        :columns-loaded="effectiveColumnsLoaded"
        :grid-items="safeGridItems"
        :data-item-key="dataItemKey"
        :grid-pageable="gridPageable"
        :sort="sort"
        :filter="filter"
        :take="take"
        :total="total"
        :skip="skip"
        :photo-url="photoUrl"
        :is-photo-loading="isPhotoLoading"
        :close-pill-class-fn="closePillClassFn"
        @filterchange="filterChange"
        @sortchange="sortChangeHandler"
        @pagechange="pageChangeHandler"
        @open-loan="openLoanTasks"
        @open-encompass="openEncompassWeb"
        @open-video="openVideo"
        @open-teams="openInTeams"
        @open-email="openEmail"
        @img-error="onUwImgError"
      />
    </div>

    <div class="conditions-dashboard__container" v-else>
      <ConditionsList
        :items="safeGridItems"
        :total="total"
        :photo-url="photoUrl"
        :is-photo-loading="isPhotoLoading"
        :close-pill-class-fn="closePillClassFn"
        :search-text="searchText"
        @search-input="onSearchTextChanged"
        @search-clear="clearSearch"
        @open-loan="openLoanTasks"
        @open-encompass="openEncompassWeb"
        @open-video="openVideo"
        @open-teams="openInTeams"
        @open-email="openEmail"
        @img-error="onUwImgError"
      />

      <div class="list-pager">
        <button class="pager-btn" :disabled="skip === 0" @click="prevPage">
          <i class="fa-duotone fa-chevron-left"></i>
          Prev
        </button>

        <div class="pager-meta">
          Page {{ currentPage }} / {{ totalPages }}
        </div>

        <button class="pager-btn" :disabled="skip + take >= total" @click="nextPage">
          Next
          <i class="fa-duotone fa-chevron-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import ConditionsGrid from "./ConditionsGrid.vue"
import ConditionsList from "./ConditionsList.vue"
import ConditionsLoanGrouped from "./ConditionsLoanGrouped.vue"
import { process } from "@progress/kendo-data-query"
import auth from "@/authConfig"

import SignalRService from "@/api/signalr.service.js"

import {
  readCachedPhoto,
  writeCachedPhoto,
  writeCachedNotFound,
  normalizePhotoSrc
} from "@/profilePhotoCache.js"

import { fetchConditionsNeedingAttention } from "@/helpers/conditionsDashboardData.js"

const LS_VIEW_MODE = "conditions_dashboard_view_mode"
const LS_GROUP_BY_LOANS = "conditions_dashboard_group_by_loans"

export default {
  name: "ConditionsDashboardMain",
  components: {
    ConditionsGrid,
    ConditionsList,
    ConditionsLoanGrouped
  },
  props: {
    allData: { type: Array, default: () => null },
    columnsLoaded: { type: Boolean, default: false },
    closePillClassFn: { type: Function, default: () => "" },
    autoFetch: { type: Boolean, default: true }
  },
  data() {
    return {
      dataSource: [],
      localColumnsLoaded: false,

      filteredAllItems: [],
      viewMode: "list",
      groupByLoans: false,

      gridItems: [],
      skip: 0,
      take: 50,
      total: 0,

      sort: [],
      filter: null,

      searchText: "",
      searchDebounce: null,

      gridPageable: {
        buttonCount: 5,
        info: true,
        type: "numeric",
        pageSizes: [10, 25, 50, 100],
        previousNext: true
      },

      uwPhotos: {},
      uwLoading: {},
      uwFailed: {},
      uwCacheTtlMs: 1000 * 60 * 60 * 24 * 7,

      signalrStarted: false,
      signalrDebounce: null,
      signalrSyncDebounce: null
    }
  },
  computed: {
    effectiveAllData() {
      if (Array.isArray(this.allData)) return this.allData
      return Array.isArray(this.dataSource) ? this.dataSource : []
    },

    effectiveColumnsLoaded() {
      if (this.columnsLoaded === true) return true
      return this.localColumnsLoaded === true
    },

    dataItemKey() {
      return "conditionId"
    },

    safeGridItems() {
      return Array.isArray(this.gridItems) ? this.gridItems : []
    },

    currentPage() {
      return Math.floor((this.skip || 0) / (this.take || 1)) + 1
    },

    totalPages() {
      return Math.max(1, Math.ceil((this.total || 0) / (this.take || 1)))
    }
  },
  watch: {
    viewMode(v) {
      localStorage.setItem(LS_VIEW_MODE, v)
    },

    effectiveAllData: {
      immediate: true,
      deep: true,
      handler() {
        this.applyState()
      }
    }
  },
  async mounted() {
    const savedView = localStorage.getItem(LS_VIEW_MODE)
    if (savedView === "grid" || savedView === "list") this.viewMode = savedView

    const savedGrouped = localStorage.getItem(LS_GROUP_BY_LOANS)
    if (savedGrouped === "1") this.groupByLoans = true
    if (savedGrouped === "0") this.groupByLoans = false

    if (!Array.isArray(this.allData) && this.autoFetch) {
      try {
        const result = await fetchConditionsNeedingAttention()
        this.dataSource = result.allData || []
      } finally {
        this.localColumnsLoaded = true
      }
    }

    await this.startSignalR()
  },
  beforeUnmount() {
    clearTimeout(this.searchDebounce)
    clearTimeout(this.signalrDebounce)
    clearTimeout(this.signalrSyncDebounce)
  },
  methods: {
    usesPropData() {
      return Array.isArray(this.allData)
    },

    async refreshFromApi() {
      if (this.usesPropData()) return
      try {
        const result = await fetchConditionsNeedingAttention()
        this.dataSource = result.allData || []
        this.localColumnsLoaded = true
        this.applyState()
      } catch (err) {
        console.error("SignalR: refreshFromApi failed", err)
      }
    },

    scheduleFullResync() {
      clearTimeout(this.signalrSyncDebounce)
      this.signalrSyncDebounce = setTimeout(() => {
        this.refreshFromApi()
      }, 800)
    },

    async startSignalR() {
      if (this.signalrStarted) return

      try {
        await SignalRService.startConditionsConnection()

        SignalRService.onConditionsReconnected(() => {
          console.log("SignalR: ConditionsHub reconnected, refreshing dashboard")
          this.scheduleFullResync()
        })

        SignalRService.onNeedsAttentionChanged(dto => {
          console.log("SignalR: NeedsAttentionChanged", dto)
          this.queueRefreshFromSignalR(dto)
          this.scheduleFullResync()
        })

        this.signalrStarted = true
        console.log("SignalR: ConditionsDashboardMain subscribed to NeedsAttentionChanged")
      } catch (err) {
        console.error("SignalR: failed to start ConditionsHub", err)
      }
    },

    queueRefreshFromSignalR(message) {
      const updated = message?.arguments?.[0] || message
      if (!updated?.conditionId) return
      if (this.usesPropData()) return

      clearTimeout(this.signalrDebounce)
      this.signalrDebounce = setTimeout(() => {
        const src = Array.isArray(this.dataSource) ? [...this.dataSource] : []
        const idx = src.findIndex(x => x.conditionId === updated.conditionId)

        const shouldRemove =
          updated.needsAttention === false ||
          updated.isActive === false

        if (shouldRemove) {
          if (idx !== -1) src.splice(idx, 1)
          this.dataSource = src
          this.applyState()
          return
        }

        if (idx !== -1) {
          src[idx] = { ...src[idx], ...updated }
        } else {
          src.unshift(updated)
        }

        this.dataSource = src
        this.localColumnsLoaded = true
        this.applyState()
      }, 100)
    },

    toggleGroupByLoans() {
      const next = !this.groupByLoans
      this.groupByLoans = next
      localStorage.setItem(LS_GROUP_BY_LOANS, next ? "1" : "0")
      this.skip = 0
      this.applyState()
    },

    onSearchTextChanged(v) {
      this.searchText = v
      this.onSearchInput()
    },

    onSearchInput() {
      clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => {
        this.skip = 0
        this.applyState()
      }, 150)
    },

    clearSearch() {
      this.searchText = ""
      this.skip = 0
      this.applyState()
    },

    searchFilterDescriptor() {
      const q = String(this.searchText || "").trim()
      if (!q) return null

      const fields = [
        "loanNumber",
        "borrowerName",
        "title",
        "attentionReason",
        "fullPropertyAddress",
        "msstatus",
        "orgId",
        "loname",
        "procname",
        "uwname",
        "attentionSetAt",
        "estimatedClosingDate"
      ]

      return {
        logic: "or",
        filters: fields.map(field => ({
          field,
          operator: "contains",
          value: q
        }))
      }
    },

    applyState() {
      const data = Array.isArray(this.effectiveAllData) ? this.effectiveAllData : []

      const searchFilter = this.searchFilterDescriptor()
      const mergedFilter =
        this.filter && searchFilter
          ? { logic: "and", filters: [this.filter, searchFilter] }
          : this.filter || searchFilter || null

      const fullState = { skip: 0, take: data.length, sort: this.sort || [], filter: mergedFilter }
      const fullResult = process(data, fullState)

      this.filteredAllItems = fullResult.data || []
      this.total = Number(fullResult.total || 0)

      const pagedState = { skip: this.skip, take: this.take, sort: this.sort || [], filter: mergedFilter }
      const pagedResult = process(data, pagedState)

      this.gridItems = pagedResult.data || []
    },

    filterChange(e) {
      this.filter = e?.filter || null
      this.skip = 0
      this.applyState()
    },

    sortChangeHandler(e) {
      this.sort = e?.sort || []
      this.applyState()
    },

    pageChangeHandler(e) {
      this.skip = e?.page?.skip ?? this.skip
      this.take = e?.page?.take ?? this.take
      this.applyState()
    },

    prevPage() {
      this.skip = Math.max(0, this.skip - this.take)
      this.applyState()
    },

    nextPage() {
      if (this.skip + this.take < this.total) {
        this.skip += this.take
        this.applyState()
      }
    },

openLoanTasks(loanId, condId) {
  const isDev =
    window.location.hostname === "localhost" ||
    window.location.hostname === "dev.swiftapp.ai"

  const baseUrl = isDev
    ? "https://dev-swift.goldstarfinancial.com"
    : "https://swift.goldstarfinancial.com"

  const url = condId
    ? `${baseUrl}?id=${encodeURIComponent(loanId)}&condid=${encodeURIComponent(condId)}`
    : `${baseUrl}?id=${encodeURIComponent(loanId)}`

  window.open(url, "_blank", "noopener,noreferrer,popup,width=1200,height=900,left=80,top=80")
},


    openVideo(condId) {
      const videoid = "be723287-aeca-43eb-be38-eceb2e25024f.mp4"
      const baseUrl = "https://uw-videos-gs.s3.us-east-1.amazonaws.com/"
      window.open(`${baseUrl}${videoid}#t=0.1`, "_blank", "noopener")
    },

   openEncompassWeb(loanId) {
    debugger;
  const baseUrl = "https://www.encompassloconnect.com/pipeline/"
  const url = `${baseUrl}${loanId}`
  window.open(url, "_blank", "noopener,noreferrer,popup,width=1200,height=900,left=80,top=80")
},
    openInTeams(member) {
      const upn = String(member?.teamsUpn || "").trim()
      if (!upn) return
      window.open(`msteams:/l/chat/0/0?users=${encodeURIComponent(upn)}`, "_blank", "noopener")
    },

    openEmail(member, item) {
      const loanNumber = item.loanNumber || "—"
      const borrower = item.borrowerName || "—"
      const to = String(member?.teamsUpn || "").trim()
      if (!to) return

      const subject = `Loan ${loanNumber} – ${borrower}`
      const body = `Loan Number: ${loanNumber} - ${borrower}\n\nHi ${member.name},\n\n{{PUT YOUR MESSAGE HERE}}\n\nThanks,`

      const mailto =
        `mailto:${encodeURIComponent(to)}` +
        `?subject=${encodeURIComponent(subject)}` +
        `&body=${encodeURIComponent(body)}`

      window.open(mailto, "_blank")
    },

    photoUrl(idOrUpn) {
      const id = String(idOrUpn || "").trim()
      if (!id) return ""
      if (!this.uwPhotos[id] && !this.uwLoading[id] && !this.uwFailed[id]) {
        this.ensurePhotoLoaded(id)
      }
      return this.uwPhotos[id] || ""
    },

    isPhotoLoading(idOrUpn) {
      const id = String(idOrUpn || "").trim()
      if (!id) return false
      return !!this.uwLoading[id]
    },

    async ensurePhotoLoaded(idOrUpn) {
      const id = String(idOrUpn || "").trim()
      if (!id) return
      if (this.uwPhotos[id]) return
      if (this.uwLoading[id]) return
      if (this.uwFailed[id]) return

      this.uwLoading = { ...this.uwLoading, [id]: true }

      try {
        const cached = await readCachedPhoto(id, this.uwCacheTtlMs)
        if (cached === "__NOT_FOUND__") {
          this.uwFailed = { ...this.uwFailed, [id]: true }
          return
        }

        if (cached) {
          const normalized = normalizePhotoSrc(cached)
          this.uwPhotos = { ...this.uwPhotos, [id]: normalized }
          return
        }

        const tokenResponse = await auth.acquireTokenResponse()
        const token = tokenResponse?.accessToken || ""
        if (!token) return

        const url = `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(id)}/photo/$value`
        const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })

        if (!resp.ok) {
          this.uwFailed = { ...this.uwFailed, [id]: true }
          await writeCachedNotFound(id)
          return
        }

        const blob = await resp.blob()

        const base64 = await new Promise(resolve => {
          const reader = new FileReader()
          reader.onloadend = () => resolve(reader.result)
          reader.readAsDataURL(blob)
        })

        const normalized = normalizePhotoSrc(base64)
        this.uwPhotos = { ...this.uwPhotos, [id]: normalized }
        await writeCachedPhoto(id, normalized)
      } catch {
        this.uwFailed = { ...this.uwFailed, [id]: true }
        await writeCachedNotFound(id)
      } finally {
        this.uwLoading = { ...this.uwLoading, [id]: false }
      }
    },

    async onUwImgError(idOrUpn) {
      const id = String(idOrUpn || "").trim()
      if (!id) return
      this.uwPhotos = { ...this.uwPhotos, [id]: "" }
      this.uwFailed = { ...this.uwFailed, [id]: true }
      await writeCachedNotFound(id)
    }
  }
}
</script>

<style scoped>
.conditions-dashboard-main {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  min-width: 1000px;
}

.conditions-dashboard__toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border-ltr);
  background: var(--background);
}

.mini-toggle {
  border: 1px solid var(--border-lt);
  background: rgba(5, 29, 58, 0.04);
  color: var(--text-primary-on-background);
  padding: 7px 10px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 950;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.12s ease, background 0.12s ease, border-color 0.12s ease;
}

.mini-toggle i {
  color: var(--notification-blue);
  --fa-primary-color: var(--notification-blue);
  --fa-secondary-color: rgba(40, 110, 255, 0.45);
  font-size: 13px;
}

.mini-toggle:hover {
  background: rgba(40, 110, 255, 0.08);
  border-color: rgba(40, 110, 255, 0.22);
  transform: translateY(-1px);
}

.mini-toggle--on {
  border-color: rgba(40, 110, 255, 0.24);
  background: rgba(40, 110, 255, 0.08);
}

.meta {
  margin-left: auto;
  font-size: 12px;
  color: var(--text-secondary-on-background);
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.meta-strong {
  font-weight: 900;
  color: var(--text-primary-on-background);
}

.conditions-dashboard__container {
  flex-grow: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
  height: calc(100vh - 120px);
  min-height: 400px;
}

.list-pager {
  padding: 10px 12px;
  border-top: 1px solid var(--border-ltr);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: var(--background);
}

.pager-btn {
  border: 1px solid var(--border-lt);
  background: var(--button);
  color: var(--on-button);
  padding: 7px 10px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.pager-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.pager-meta {
  font-size: 12px;
  color: var(--text-secondary-on-background);
  font-weight: 800;
}
</style>
